import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MessageCircle, ChevronRight, X, Loader2, Package } from 'lucide-react';
import { groupLabelForCanonical } from '@/lib/inquiryCategory';

// Phase 12-1/12-2 · Consumer's own inquiry list (마이페이지 → 내 문의)
// STEP 5-CT-B · 소비자 표시 label 을 5개 그룹으로 통일 (기존 canonical 8종 저장값 무변경)

const ST_LABEL = { new: '답변대기', in_progress: '처리중', answered: '답변완료', closed: '종료' };
const fmt = (d) => { try { return new Date(d).toLocaleString('ko-KR', { hour12: false }); } catch { return '-'; } };

export default function MyInquiries() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/my/inquiries', { params: { status: status || undefined } });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || '문의 조회 실패'); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16">
      <div className="max-w-3xl mx-auto p-4 space-y-4" data-testid="my-inquiries-page">
        <div className="flex items-center gap-2 pt-4">
          <MessageCircle size={16} className="text-[#FF3B30]" />
          <h1 className="text-xl font-black">내 문의</h1>
        </div>

        <div className="flex gap-1 flex-wrap">
          {[
            { k: '', label: '전체' },
            { k: 'new', label: '답변대기' },
            { k: 'in_progress', label: '처리중' },
            { k: 'answered', label: '답변완료' },
            { k: 'closed', label: '종료' },
          ].map((t) => (
            <button key={t.label} onClick={() => setStatus(t.k)}
              data-testid={`my-inq-filter-${t.k || 'all'}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-full border ${status === t.k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-xs text-gray-500 py-12"><Loader2 size={16} className="animate-spin inline" /></p>
        ) : rows.length === 0 ? (
          <p className="text-center text-xs text-gray-500 py-12">등록한 문의가 없습니다.</p>
        ) : (
          <ul className="grid gap-2" data-testid="my-inq-list">
            {rows.map((i) => (
              <li key={i.inquiry_id}>
                <button onClick={() => setSelected(i)} data-testid={`my-inq-row-${i.inquiry_id}`}
                  className="w-full text-left p-3 rounded-2xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40">
                  <div className="flex gap-2 items-center flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold">{groupLabelForCanonical(i.category)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${i.status === 'answered' ? 'bg-emerald-500 text-white' : i.status === 'new' ? 'bg-orange-500 text-white' : 'bg-black/5 dark:bg-white/10'}`}>{ST_LABEL[i.status]}</span>
                    {i.unread_answer && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white font-black" data-testid={`my-inq-new-${i.inquiry_id}`}>
                        새 답변
                      </span>
                    )}
                    {i.order_number && <span className="text-[10px] text-gray-500">주문 {i.order_number}</span>}
                    {i.seller_name && <span className="text-[10px] text-gray-500">{i.seller_name}</span>}
                  </div>
                  <p className="mt-1 font-black text-sm truncate">{i.title}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{fmt(i.created_at)}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && <MyInquiryDetail inq={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function MyInquiryDetail({ inq, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => {
    try {
      const r = await api.get(`/my/inquiries/${inq.inquiry_id}`);
      setD(r.data);
      // Phase 13-D · mark read so 새 답변 badge is cleared next reload.
      api.post(`/my/inquiries/${inq.inquiry_id}/read`).catch(() => {});
    }
    catch (e) { toast.error(e?.response?.data?.detail || '조회 실패'); onClose(); }
  })(); }, [inq.inquiry_id, onClose]);

  if (!d) return <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center" onClick={onClose}><Loader2 size={20} className="animate-spin text-white" /></div>;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex" onClick={onClose} data-testid="my-inq-detail">
      <div className="ml-auto w-full sm:max-w-lg bg-white dark:bg-[#0A0A0A] h-full overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start p-4 border-b border-black/10 dark:border-white/10 shrink-0">
          <div className="min-w-0">
            <div className="flex gap-1 items-center flex-wrap">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold">{groupLabelForCanonical(d.category)}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${d.status === 'answered' ? 'bg-emerald-500 text-white' : d.status === 'new' ? 'bg-orange-500 text-white' : 'bg-black/5 dark:bg-white/10'}`}>{ST_LABEL[d.status]}</span>
            </div>
            <h3 className="text-lg font-black mt-1" data-testid="my-inq-detail-title">{d.title}</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">{fmt(d.created_at)} · {d.seller_name || '판매자'}</p>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {d.related_order && (
            <Link to={`/orders/${d.order_id}`} className="block p-3 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40" data-testid="my-inq-related-order">
              <p className="text-[10px] text-gray-500 inline-flex items-center gap-1"><Package size={11} /> 관련 주문</p>
              <p className="font-black text-sm mt-0.5">{d.related_order.order_number}</p>
              <p className="text-[11px] text-gray-500 mt-1">
                {(d.related_order.items || []).slice(0, 2).map((it) => `${it.product_name} × ${it.quantity}`).join(' · ')}
              </p>
            </Link>
          )}
          <div className="p-3 rounded-xl border border-black/10 dark:border-white/10">
            <p className="text-xs whitespace-pre-wrap">{d.content}</p>
            {Array.isArray(d.attachments) && d.attachments.length > 0 && (
              <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/5" data-testid="my-inq-detail-attachments">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">사진 첨부 ({d.attachments.length})</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                  {d.attachments.map((a, i) => {
                    const href = typeof a === 'string' ? a : (a?.url || '');
                    if (!href) return null;
                    return (
                      <a key={i} href={href} target="_blank" rel="noreferrer"
                         className="relative aspect-square rounded-lg overflow-hidden border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 hover:border-[#FF3B30]/60"
                         data-testid={`my-inq-detail-attach-${i}`}>
                        <img src={href} alt={`첨부 ${i+1}`} className="w-full h-full object-cover" loading="lazy"/>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {(d.answers || []).map((a) => (
            <div key={a.answer_id} className="p-3 rounded-xl bg-[#FF3B30]/5 border border-[#FF3B30]/20">
              <p className="text-[10px] font-bold text-[#FF3B30]">판매자 답변 · {fmt(a.created_at)}</p>
              <p className="text-xs mt-1 whitespace-pre-wrap">{a.content}</p>
            </div>
          ))}
          {(!d.answers || d.answers.length === 0) && (
            <p className="text-center text-[11px] text-gray-500 py-4">아직 판매자의 답변이 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
}
